import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import checkicon from '../../images/checkicon.svg'
import track from '../../lib/track'
import leadAttribution from '../../lib/attribution'
import './seoCheck.scss'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myzkobag'
const CHECK_COUNT = 43 // keep in step with CHECKS in api/seo-check.js
const CATS = ['technical', 'content', 'site', 'links', 'social', 'ai']
const STATUS_ORDER = { fail: 0, warn: 1, pass: 2 }
const SPEED_SCORES = ['performance', 'seo', 'accessibility', 'bestPractices']
const METRICS = ['lcp', 'fcp', 'tbt', 'cls', 'si']

// "{words} words" + { words: 12 } → "12 words"
const fill = (text, values = {}) =>
  String(text || '').replace(/\{(\w+)\}/g, (_, key) => (values[key] != null ? values[key] : ''))

const cut = (text, max) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text)

const level = (score) => (score == null ? 'none' : score >= 0.9 ? 'good' : score >= 0.5 ? 'mid' : 'bad')

/**
 * Free SEO check.
 *
 * The visitor types an address and /api/seo-check fetches and scores the page
 * (and up to 20 pages it links to) against 43 checks; /api/seo-speed then adds Google Lighthouse mobile speed.
 * Every problem comes with a plain-language fix. The result doubles as the
 * brief for the lead form: the team receives the URL, the score, the speed
 * numbers and the exact list of problems, ready for a priced fix plan.
 */
const SeoCheck = () => {
  const { translations: t, selectedLanguage } = useContext(LanguageContext)
  const animateIn = useEntryMotion()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [url, setUrl] = useState(params.get('url') || '')
  const [state, setState] = useState('idle') // idle | loading | done | error
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const [speed, setSpeed] = useState(null)
  const [speedState, setSpeedState] = useState('idle')
  const [step, setStep] = useState(0)
  const [lead, setLead] = useState({ name: '', email: '' })
  const [leadStatus, setLeadStatus] = useState('idle')
  const resultRef = useRef(null)
  const runId = useRef(0)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // The loading steps are cosmetic, but they tell the visitor what is being
  // looked at while the request runs.
  useEffect(() => {
    if (state !== 'loading') return undefined
    setStep(0)
    const timer = setInterval(() => setStep((s) => Math.min(s + 1, 3)), 1500)
    return () => clearInterval(timer)
  }, [state])

  const runSpeed = async (pageUrl, id = runId.current) => {
    setSpeedState('loading')
    setSpeed(null)
    try {
      const res = await fetch('/api/seo-speed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: pageUrl, lang: selectedLanguage }),
      })
      const data = await res.json().catch(() => ({}))
      if (id !== runId.current) return
      if (!res.ok || !data.scores) {
        setSpeedState('error')
        return
      }
      setSpeed(data)
      setSpeedState('done')
    } catch (err) {
      if (id === runId.current) setSpeedState('error')
    }
  }

  const run = async (event) => {
    if (event) event.preventDefault()
    if (!url.trim() || state === 'loading') return
    const id = ++runId.current
    setState('loading')
    setError(null)
    setLeadStatus('idle')
    setSpeedState('idle')
    try {
      const res = await fetch('/api/seo-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (id !== runId.current) return
      if (!res.ok || !data.checks) {
        setError(data)
        setState('error')
        return
      }
      setResult(data)
      setState('done')
      // Keep the address in the URL so the report can be shared as a link.
      setParams({ url: url.trim() }, { replace: true })
      track('seo_check_run', { score: data.score })
      runSpeed(data.finalUrl, id)
      setTimeout(() => resultRef.current && resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
    } catch (err) {
      setError({})
      setState('error')
    }
  }

  // /seo-check?url=company.com opens straight into that company's report.
  const autoRan = useRef(false)
  useEffect(() => {
    if (autoRan.current || !params.get('url')) return
    autoRan.current = true
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkText = (c) => {
    const base = `seoc.${c.id}`
    if (c.status === 'pass') {
      const okKey = c.value.total === 0 && t[`${base}.none`] ? `${base}.none` : `${base}.ok`
      return fill(t[okKey] || t[`${base}.ok`], c.value)
    }
    return fill((c.status === 'fail' && t[`${base}.fail`]) || t[`${base}.bad`], c.value)
  }

  const counts = result
    ? result.checks.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {})
    : {}

  // "Solid" only when nothing critical is left; one failed check is a gap worth fixing.
  const band = !result ? null : result.score >= 85 && !counts.fail ? 'good' : result.score >= 60 ? 'mid' : 'low'

  const problems = result
    ? result.checks
        .filter((c) => c.status !== 'pass')
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.weight - a.weight)
    : []

  const submitLead = async (event) => {
    event.preventDefault()
    if (leadStatus === 'sending') return
    setLeadStatus('sending')
    const list = problems.map((c) => `[${c.status.toUpperCase()}] ${t[`seoc.${c.id}.name`]}: ${checkText(c)}`).join('\n')
    const speedText = speed
      ? `\n\nMobile speed (Lighthouse): performance ${speed.scores.performance}, SEO ${speed.scores.seo}, ` +
        `LCP ${speed.metrics.lcp && speed.metrics.lcp.display}, TBT ${speed.metrics.tbt && speed.metrics.tbt.display}, ` +
        `CLS ${speed.metrics.cls && speed.metrics.cls.display}\nTop wins: ${speed.opportunities.map((o) => `${o.title} (−${(o.savingsMs / 1000).toFixed(1)} s)`).join('; ')}`
      : ''
    const f = result.facts
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `SEO check lead: ${result.finalUrl} (score ${result.score}/100)`,
          name: lead.name,
          email: lead.email,
          website: result.finalUrl,
          score: `${result.score} / 100`,
          platform: [f.platform, f.server].filter(Boolean).join(' / ') || 'unknown',
          message: (list || 'No problems found — wants a deeper audit.') + speedText,
          source: 'Free SEO check',
          ...leadAttribution(),
        }),
      })
      setLeadStatus(res.ok ? 'sent' : 'error')
      if (res.ok) track('seo_check_lead', { score: result.score })
    } catch (err) {
      setLeadStatus('error')
    }
  }

  const errorText = error ? fill(t[`seocErr.${error.error}`] || t['seocErr.default'], error) : ''
  const steps = [t.seocStep1, t.seocStep2, t.seocStep3, t.seocStep4]
  const radius = 54
  const circumference = 2 * Math.PI * radius
  const facts = result && result.facts

  let serpPath = ''
  if (result) {
    const u = new URL(result.finalUrl)
    serpPath = [u.hostname, ...u.pathname.split('/').filter(Boolean)].join(' › ')
  }

  const reset = () => {
    runId.current++
    setState('idle')
    setResult(null)
    setSpeed(null)
    setSpeedState('idle')
    setUrl('')
    setParams({}, { replace: true })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Layout>
      <Seo
        title={t.seocSeoTitle}
        description={fill(t.seocSeoDescription, { n: CHECK_COUNT })}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Free SEO check', path: '/seo-check' },
          ]),
        ]}
      />

      <div className='seoc-page'>
        <div className='seoc-content'>
          <p className='seoc-kicker'>{t.seocKicker}</p>
          <h1 className='seoc-title'>{t.seocTitle}</h1>
          <p className='seoc-sub'>{fill(t.seocSub, { n: CHECK_COUNT })}</p>

          <form className='seoc-form' onSubmit={run}>
            <input
              type='text'
              inputMode='url'
              autoComplete='url'
              spellCheck='false'
              aria-label={t.seocPlaceholder}
              placeholder={t.seocPlaceholder}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <button type='submit' disabled={state === 'loading'}>
              {state === 'loading' ? t.seocChecking : t.seocButton}
            </button>
          </form>

          {state === 'loading' && (
            <ul className='seoc-steps' aria-live='polite'>
              {steps.map((label, i) => (
                <li key={label} className={i < step ? 'is-done' : i === step ? 'is-active' : ''}>
                  <span className='seoc-step-dot' aria-hidden='true' />
                  {label}
                </li>
              ))}
            </ul>
          )}

          {state === 'error' && <p className='seoc-error' role='alert'>{errorText}</p>}

          <AnimatePresence>
            {state === 'done' && result && (
              <motion.div
                ref={resultRef}
                className='seoc-result'
                initial={animateIn ? { opacity: 0, y: 24 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Score */}
                <div className='seoc-summary'>
                  <div className={`seoc-ring seoc-ring-${band}`}>
                    <svg viewBox='0 0 120 120' aria-hidden='true'>
                      <circle cx='60' cy='60' r={radius} className='seoc-ring-track' />
                      <motion.circle
                        cx='60'
                        cy='60'
                        r={radius}
                        className='seoc-ring-value'
                        strokeDasharray={circumference}
                        initial={animateIn ? { strokeDashoffset: circumference } : false}
                        animate={{ strokeDashoffset: circumference * (1 - result.score / 100) }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </svg>
                    <div className='seoc-ring-label'>
                      <span className='seoc-ring-score'>{result.score}</span>
                      <span className='seoc-ring-caption'>{t.seocScoreLabel}</span>
                    </div>
                  </div>
                  <div className='seoc-summary-text'>
                    <p className='seoc-url'>{result.finalUrl}</p>
                    <h2>{t[`seocBand.${band}.title`]}</h2>
                    <p>{t[`seocBand.${band}.text`]}</p>
                    <div className='seoc-counts'>
                      <span className='seoc-count seoc-count-fail'>{counts.fail || 0} {t.seocFailed}</span>
                      <span className='seoc-count seoc-count-warn'>{counts.warn || 0} {t.seocWarn}</span>
                      <span className='seoc-count seoc-count-pass'>{counts.pass || 0} {t.seocPassed}</span>
                    </div>
                  </div>
                </div>

                {/* Priorities */}
                {problems.length > 0 && (
                  <div className='seoc-top'>
                    <h3 className='seoc-block-title'>{t.seocTopFix}</h3>
                    <ol>
                      {problems.slice(0, 3).map((c) => (
                        <li key={c.id} className={`seoc-top-item seoc-top-${c.status}`}>
                          <strong>{t[`seoc.${c.id}.name`]}</strong>
                          <span>{t[`seoc.${c.id}.fix`]}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Google preview + facts */}
                <div className='seoc-two'>
                  <div className='seoc-panel'>
                    <h3 className='seoc-block-title'>{t.seocSerpTitle}</h3>
                    <div className='seoc-serp'>
                      <div className='seoc-serp-site'>
                        <img src={facts.favicon} alt='' width='26' height='26' onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                        <div>
                          <span className='seoc-serp-name'>{new URL(result.finalUrl).hostname.replace(/^www\./, '')}</span>
                          <span className='seoc-serp-path'>{serpPath}</span>
                        </div>
                      </div>
                      <p className='seoc-serp-title'>{cut(facts.title || result.finalUrl, 60)}</p>
                      <p className='seoc-serp-desc'>{facts.description ? cut(facts.description, 160) : <em>{t.seocSerpNoDesc}</em>}</p>
                    </div>
                    <p className='seoc-small'>{t.seocSerpNote}</p>
                  </div>

                  <div className='seoc-panel'>
                    <h3 className='seoc-block-title'>{t.seocFactsTitle}</h3>
                    <dl className='seoc-facts'>
                      {[
                        ['platform', facts.platform || t.seocUnknown],
                        ['server', facts.server || t.seocUnknown],
                        ['ttfb', `${facts.ttfb} ms`],
                        ['kb', `${facts.kb} KB`],
                        ['words', facts.words],
                        ['images', facts.images],
                        ['links', `${facts.internal} / ${facts.external}`],
                        ['sitemap', facts.sitemapUrls || '—'],
                        ['lang', facts.lang || t.seocUnknown],
                      ].map(([key, value]) => (
                        <div key={key}>
                          <dt>{t[`seocFact.${key}`]}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>

                {/* Lighthouse */}
                <section className='seoc-speed'>
                  <h3 className='seoc-block-title'>{t.seocSpeedTitle}</h3>
                  {speedState === 'loading' && (
                    <div className='seoc-speed-loading' aria-live='polite'>
                      <span className='seoc-spinner' aria-hidden='true' />
                      <p>{t.seocSpeedLoading}</p>
                    </div>
                  )}
                  {speedState === 'error' && (
                    <div className='seoc-speed-loading'>
                      <p>{t.seocSpeedError}</p>
                      <button type='button' className='seoc-link-btn' onClick={() => runSpeed(result.finalUrl)}>
                        ↻ {t.seocSpeedRetry}
                      </button>
                    </div>
                  )}
                  {speedState === 'done' && speed && (
                    <>
                      <div className='seoc-gauges'>
                        {SPEED_SCORES.map((key) => {
                          const value = speed.scores[key]
                          return (
                            <div key={key} className={`seoc-gauge seoc-lvl-${level(value == null ? null : value / 100)}`}>
                              <span className='seoc-gauge-value'>{value == null ? '—' : value}</span>
                              <span className='seoc-gauge-label'>{t[`seocScore.${key}`]}</span>
                            </div>
                          )
                        })}
                      </div>

                      <ul className='seoc-metrics'>
                        {METRICS.filter((m) => speed.metrics[m]).map((m) => (
                          <li key={m} className={`seoc-lvl-${level(speed.metrics[m].score)}`}>
                            <span className='seoc-metric-value'>{speed.metrics[m].display}</span>
                            <span className='seoc-metric-name'>{t[`seocMetric.${m}`]}</span>
                            <span className='seoc-metric-hint'>{t[`seocMetricHint.${m}`]}</span>
                          </li>
                        ))}
                      </ul>

                      {speed.field && (
                        <div className='seoc-field'>
                          <p className='seoc-field-title'>{t.seocFieldTitle}</p>
                          <div className='seoc-field-row'>
                            {['lcp', 'inp', 'cls'].filter((k) => speed.field[k]).map((k) => (
                              <span key={k} className={`seoc-field-chip seoc-field-${speed.field[k].category}`}>
                                {k.toUpperCase()}: {k === 'cls' ? speed.field[k].value : `${(speed.field[k].value / 1000).toFixed(k === 'inp' ? 2 : 1)} s`} · {t[`seocField.${speed.field[k].category}`] || speed.field[k].category}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {speed.opportunities.length > 0 && (
                        <div className='seoc-opps'>
                          <p className='seoc-field-title'>{t.seocOppTitle}</p>
                          <ul>
                            {speed.opportunities.map((o) => (
                              <li key={o.id}>
                                <span>{o.title}</span>
                                <strong>{fill(t.seocOppSave, { s: (o.savingsMs / 1000).toFixed(1) })}</strong>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {speed.pageKb != null && <p className='seoc-small'>{fill(t.seocPageWeight, { kb: speed.pageKb })}</p>}
                    </>
                  )}
                </section>

                {/* All checks */}
                {CATS.map((cat) => (
                  <section key={cat} className='seoc-cat'>
                    <h3 className='seoc-cat-title'>{t[`seocCat.${cat}`]}</h3>
                    <ul className='seoc-checks'>
                      {result.checks
                        .filter((c) => c.cat === cat)
                        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
                        .map((c) => (
                          <li key={c.id} className={`seoc-check seoc-check-${c.status}`}>
                            <span className='seoc-check-icon' aria-hidden='true'>
                              {c.status === 'pass' ? '✓' : c.status === 'warn' ? '!' : '✕'}
                            </span>
                            <div>
                              <p className='seoc-check-name'>{t[`seoc.${c.id}.name`]}</p>
                              <p className='seoc-check-text'>{checkText(c)}</p>
                              {c.status !== 'pass' && (
                                <p className='seoc-check-fix'>
                                  <strong>{t.seocHowFix}</strong> {t[`seoc.${c.id}.fix`]}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                    </ul>
                  </section>
                ))}

                {/* Pages read for the site-wide checks */}
                {facts.pages.length > 0 && (
                  <div className='seoc-panel seoc-pages'>
                    <h3 className='seoc-block-title'>{t.seocPagesTitle}</h3>
                    <div className='seoc-pages-scroll'>
                      <table>
                        <thead>
                          <tr>
                            {['page', 'title', 'desc', 'h1', 'words'].map((col) => (
                              <th key={col}>{t[`seocPagesCol.${col}`]}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {facts.pages.map((p) => (
                            <tr key={p.path}>
                              <td className='seoc-pages-path'>
                                {p.path}
                                {p.noindex && <span className='seoc-pages-tag'>noindex</span>}
                              </td>
                              <td className={p.title ? '' : 'is-bad'}>{p.title || '—'}</td>
                              <td className={p.description ? 'is-good' : 'is-bad'}>{p.description ? '✓' : '✕'}</td>
                              <td className={p.h1 === 1 ? 'is-good' : 'is-bad'}>{p.h1}</td>
                              <td className={p.words < 120 ? 'is-bad' : ''}>{p.words}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className='seoc-small'>{t.seocPagesNote}</p>
                  </div>
                )}

                {/* Structure and keywords */}
                <div className='seoc-two seoc-two-top'>
                  <div className='seoc-panel'>
                    <h3 className='seoc-block-title'>{t.seocOutlineTitle}</h3>
                    {facts.outline.length ? (
                      <ul className='seoc-outline'>
                        {facts.outline.map((h, i) => (
                          <li key={`${h.level}-${i}`} style={{ paddingLeft: `${(h.level - 1) * 14}px` }}>
                            <span className='seoc-outline-tag'>H{h.level}</span> {h.text}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className='seoc-small'>{t.seocOutlineEmpty}</p>
                    )}
                  </div>
                  <div className='seoc-panel'>
                    <h3 className='seoc-block-title'>{t.seocKeywordsTitle}</h3>
                    <div className='seoc-keywords'>
                      {facts.keywords.map((k) => (
                        <span key={k.word}>
                          {k.word} <em>{k.count}</em>
                        </span>
                      ))}
                    </div>
                    <p className='seoc-small'>{t.seocKeywordsNote}</p>
                  </div>
                </div>

                <p className='seoc-note'>{t.seocNote}</p>

                {/* Lead */}
                <div className='seoc-lead'>
                  {leadStatus === 'sent' ? (
                    <div className='seoc-sent'>
                      <img src={checkicon} alt='' width='40' height='40' />
                      <div>
                        <h3>{t.seocSentTitle}</h3>
                        <p>{t.seocSentText}</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={submitLead}>
                      <h3 className='seoc-lead-title'>{t.seocLeadTitle}</h3>
                      <p className='seoc-lead-sub'>{t.seocLeadSub}</p>
                      <div className='seoc-lead-row'>
                        <label>
                          <span>{t.quoteName}</span>
                          <input
                            type='text'
                            value={lead.name}
                            autoComplete='name'
                            required
                            onChange={(e) => setLead({ ...lead, name: e.target.value })}
                          />
                        </label>
                        <label>
                          <span>{t.quoteEmail}</span>
                          <input
                            type='email'
                            value={lead.email}
                            autoComplete='email'
                            required
                            onChange={(e) => setLead({ ...lead, email: e.target.value })}
                          />
                        </label>
                      </div>
                      <button type='submit' className='seoc-lead-btn' disabled={leadStatus === 'sending'}>
                        {leadStatus === 'sending' ? t.quoteSending : t.seocLeadBtn}
                      </button>
                      {leadStatus === 'error' && <p className='seoc-error'>{t['seocErr.default']}</p>}
                      <p className='seoc-lead-note'>{t.quoteNote}</p>
                    </form>
                  )}
                  <div className='seoc-lead-links'>
                    <button type='button' onClick={() => navigate('/services/seo')}>{t.seocSeeService} →</button>
                    <button type='button' onClick={() => navigate(`/accessibility-check?url=${encodeURIComponent(result.finalUrl)}`)}>
                      {t.a11yButton} →
                    </button>
                    <button type='button' onClick={reset}>↻ {t.seocAgain}</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {state !== 'done' && (
            <ul className='seoc-what'>
              {[t.seocWhat1, t.seocWhat2, t.seocWhat3].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default SeoCheck
